import React, { useEffect } from "react";
import { MapContainer, TileLayer, CircleMarker, Circle, Tooltip, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { SEARCH, MAP_PIN, MAP_LEGEND } from "../../../core/theme/searchTheme";
import { mainArea } from "../../../core/data/scanPlan";
import { useFoldingCredit, NoLeafletPrefix } from "../../../core/layout/mapCredit";

const UK_CENTRE = [54.2, -2.5];
const UK_ZOOM = 6;
const SINGLE_PIN_ZOOM = 12;
const MAX_FIT_ZOOM = 13;
const METRES_PER_MILE = 1609.344;
const MILES_PER_DEGREE = 69.05;

// Fits the map to the pins whenever they change; the whole UK when there are none.
function FitToPins({ points }) {
  const map = useMap();
  const key = points.map(p => p.join(",")).join("|");

  // The map's box is sized by the page layout, so measure again once it's on screen.
  useEffect(() => {
    const timer = setTimeout(() => map.invalidateSize(), 0);
    return () => clearTimeout(timer);
  }, [map]);

  useEffect(() => {
    if (points.length === 0) map.setView(UK_CENTRE, UK_ZOOM);
    else if (points.length === 1) map.setView(points[0], SINGLE_PIN_ZOOM);
    else map.fitBounds(points, { padding: [48, 48], maxZoom: MAX_FIT_ZOOM });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, key]);

  return null;
}

// The top, bottom, left and right edges of a circle, so the map fits it.
function circleEdges(c, miles) {
  const dLat = miles / MILES_PER_DEGREE;
  const dLng = miles / (MILES_PER_DEGREE * Math.cos((c.lat * Math.PI) / 180));
  return [[c.lat + dLat, c.lng], [c.lat - dLat, c.lng], [c.lat, c.lng + dLng], [c.lat, c.lng - dLng]];
}

// Right-hand map: a soft purple pin per postcode searched, a stronger ringed
// purple pin for each business location, and the radius round each one.
// postcodes: what the scan will search; centres: the business locations
// (businessLocations); radius: miles or null.
export default function SearchMap({ postcodes, centres, radius, business }) {
  const areaPins = postcodes.filter(p => p.lat != null && p.lng != null);
  const points = [
    ...areaPins.map(p => [p.lat, p.lng]),
    ...centres.map(c => [c.lat, c.lng]),
    ...(radius ? centres.flatMap(c => circleEdges(c, radius)) : []),
  ];
  const area = mainArea(postcodes);
  const [creditFolded, foldCredit] = useFoldingCredit();

  return (
    <div className={SEARCH.mapColumn}>
      <div className={SEARCH.mapHeader}>
        <div className="flex items-center gap-2 min-w-0">
          <span className={SEARCH.mapTitle}>{area || "Map"}</span>
          <span className={SEARCH.mapMuted}>{postcodes.length} postcode{postcodes.length === 1 ? "" : "s"}</span>
        </div>
        <div className="flex items-center gap-4 text-slate-500 shrink-0">
          <span className="inline-flex items-center gap-1.5"><span className={MAP_LEGEND.area} /> Area searched</span>
          <span className="inline-flex items-center gap-1.5"><span className={MAP_LEGEND.business} /> Your business</span>
        </div>
      </div>
      <div className={`${SEARCH.mapArea} ${creditFolded ? "map-credit-folded" : ""}`} onPointerDown={foldCredit} onWheel={foldCredit}>
        <MapContainer center={UK_CENTRE} zoom={UK_ZOOM} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
          <NoLeafletPrefix />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {radius && centres.map(c => (
            <Circle key={`circle-${c.postcode}`} center={[c.lat, c.lng]} radius={radius * METRES_PER_MILE} pathOptions={MAP_PIN.radiusCircle} interactive={false} />
          ))}
          {areaPins.map(p => (
            <CircleMarker key={p.postcode} center={[p.lat, p.lng]} pathOptions={MAP_PIN.area} radius={MAP_PIN.area.radius}>
              <Tooltip permanent direction="right" offset={[8, 0]} className={MAP_PIN.label}>{p.postcode}</Tooltip>
            </CircleMarker>
          ))}
          {centres.map(c => (
            <React.Fragment key={`business-${c.postcode}`}>
              <CircleMarker center={[c.lat, c.lng]} pathOptions={MAP_PIN.businessRing} radius={MAP_PIN.businessRing.radius} />
              <CircleMarker center={[c.lat, c.lng]} pathOptions={MAP_PIN.businessDot} radius={MAP_PIN.businessDot.radius}>
                <Tooltip permanent direction="right" offset={[12, 0]} className={MAP_PIN.label}>
                  {c.isMain ? business.name.trim() || c.postcode : c.postcode}
                </Tooltip>
              </CircleMarker>
            </React.Fragment>
          ))}
          <FitToPins points={points} />
        </MapContainer>
      </div>
    </div>
  );
}
